import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useApi } from '../api/api-context';
import { buildNodeRoute } from '../state/route';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';

const STARTING_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export function RepertoireManager({ activeId }: { activeId?: string }): JSX.Element {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [side, setSide] = useState<'white' | 'black'>('white');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data, isError } = useQuery({
    queryKey: ['repertoires'],
    queryFn: () => api.listRepertoires(),
  });

  function select(repertoire: NonNullable<typeof data>['items'][number]): void {
    const root = repertoire.defaultRoot.position;
    navigate(
      buildNodeRoute({
        repertoireId: repertoire.repertoireId,
        zobristHash: root.zobristHash,
        positionId: root.positionId,
        path: null,
      }),
    );
  }

  async function onCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data: created } = await api.createRepertoire(
        { name: name.trim(), side, visibility: 'private', rootFen: STARTING_FEN, settings: {} },
        crypto.randomUUID(),
      );
      await queryClient.invalidateQueries({ queryKey: ['repertoires'] });
      setName('');
      setOpen(false);
      select(created);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create repertoire. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="studio-repertoire flex min-w-0 items-center gap-3">
      {activeId && (
        <select
          aria-label="Current repertoire"
          value={activeId}
          disabled={!data}
          onChange={(e) => {
            const selected = data?.items.find((r) => r.repertoireId === e.target.value);
            if (selected) select(selected);
          }}
        >
          {!data && (
            <option value={activeId}>
              {isError ? 'Repertoires unavailable' : 'Loading repertoire…'}
            </option>
          )}
          {(data?.items ?? []).map((r) => (
            <option key={r.repertoireId} value={r.repertoireId}>
              {r.name}
            </option>
          ))}
        </select>
      )}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!busy) {
            setOpen(next);
            setError(null);
          }
        }}
      >
        <DialogTrigger asChild>
          <Button type="button" variant="outline">
            <Plus size={16} />
            <span>New repertoire</span>
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogTitle className="text-xl font-semibold pr-6">Create your repertoire</DialogTitle>
          <DialogDescription className="text-sm text-slate-300">
            Give your opening collection a name and choose the side you play.
          </DialogDescription>
          <form onSubmit={(e) => void onCreate(e)} aria-label="Create repertoire">
            <div className="field">
              <label htmlFor="rep-name">Repertoire name</label>
              <input
                id="rep-name"
                type="text"
                required
                maxLength={200}
                value={name}
                disabled={busy}
                onChange={(e) => setName(e.target.value)}
                placeholder="My Caro-Kann repertoire"
              />
            </div>
            <div className="field">
              <label htmlFor="rep-side">Side</label>
              <select
                id="rep-side"
                disabled={busy}
                value={side}
                onChange={(e) => setSide(e.target.value as 'white' | 'black')}
              >
                <option value="white">White</option>
                <option value="black">Black</option>
              </select>
            </div>
            {error && (
              <p role="alert" className="error">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={!name.trim() || busy}>
              {busy ? 'Creating…' : 'Create repertoire'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
